import { FETCH_CROPS } from '@dehaat/kisan-app-bl/api/fetchAllCropsV2';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import CropCategoryAccordion from '@/components/CropCategoryAccordion';
import ErrorModal from '@/components/ErrorModal';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import LazyImage from '@/components/LazyImage';
import Okay from '@/components/modalActions/Okay';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SearchBox from '@/components/SearchBox';
import { ACCESS_TOKEN_KEY, WEBSITE_URL } from '@/constants/common';
import useDebounce from '@/hooks/useDebounce';
import useErrorModalState from '@/hooks/useErrorModalState';
import fetchAllCropsV2 from '@/utils/api/fetchCropsV2';
interface Props {
  allCrops: CropsWithCategoryProps[];
}
const AllCropsPage = ({
  allCrops
}: Props) => {
  const {
    t
  } = useTranslation('crop');
  const {
    locale,
    push
  } = useRouter();
  const cropsRef = useRef<Crop[]>([]);
  const {
    errorModalState,
    hideErrorModal
  } = useErrorModalState();
  const [filteredCrops, setFilteredCrops] = useState<Crop[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const handleCropSelect = (crop: Crop) => {
    const slugEnc = encodeURIComponent(crop.slug.slug);
    push(`/crop/${slugEnc}/problems`);
  };
  const handleSearch = useCallback((newSearchQuery: string) => {
    if (newSearchQuery) {
      const newFilteredCrops: Crop[] = [];
      const searchQueryRegex = new RegExp(newSearchQuery, 'i');
      cropsRef.current.forEach(crop => {
        if (searchQueryRegex.test(crop.name) || searchQueryRegex.test(crop.app_display_name)) {
          newFilteredCrops.push(crop);
        }
      });
      setFilteredCrops(newFilteredCrops);
    } else {
      setFilteredCrops([]);
    }
  }, []);
  const debouncedSearch = useDebounce(handleSearch, 500);
  useEffect(() => {
    if (isSearching) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isSearching]);
  useEffect(() => {
    let crops: Crop[] = [];
    allCrops.forEach(category => crops = category.crops.reduce((prev, crop) => crop.is_khetibook_enabled ? prev.concat(crop) : prev, crops));
    cropsRef.current = crops;
  }, [allCrops]);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="crops.tsx">
        <title>All Crops | DeHaat Kisan: Crop Guide & Advisory</title>
        <meta name="description" content="Explore crop-wise farming guides, disease management, and expert advisory for all major Indian crops on DeHaat — wheat, rice, cotton, sugarcane, vegetables and more." data-sentry-element="meta" data-sentry-source-file="crops.tsx" />
        <link rel="canonical" href={`${WEBSITE_URL}${locale === DEFAULT_LANG ? '' : `/${locale}`}/crops`} />
        <link rel="alternate" hrefLang={`${locale}-in`} href={`${WEBSITE_URL}${locale === DEFAULT_LANG ? '' : `/${locale}`}/crops`} />
      </Head>
      <main className="relative h-screen">
        {<HeaderWithBackButton heading={t('crops')} />}

        <section className="w-full p-4 m-auto pt-18 max-w-7xl pb-16">
        <div className={`bg-white pt-6 ${isSearching ? 'h-screen w-full z-50' : ''}`}>
          <SearchBox lang={locale || DEFAULT_LANG} onBackClick={() => setIsSearching(false)} onFocus={() => setIsSearching(true)} onSearch={debouncedSearch} placeholder={t('type_crop_name')} showBackBtn={isSearching} data-sentry-element="SearchBox" data-sentry-source-file="crops.tsx" />

          {isSearching && filteredCrops.length > 0 && <section className="px-2 py-4" data-testid="suggestion-section">
              <Paragraph className="text-neutral-50" isHighlighted>
                {t('suggestions')}
              </Paragraph>
              {filteredCrops.map(crop => <button className="flex items-center gap-2 mt-4" key={crop.id} data-testid={`suggestion-crop-${crop.id}`} onClick={() => handleCropSelect(crop)}>
                  <div className="relative w-10 h-10 overflow-hidden border rounded-full border-neutral-40">
                    <LazyImage alt={crop.app_display_name} fallBackIcon={<div className="flex items-center justify-center w-10 h-10 overflow-hidden text-2xl rounded-full bg-neutral-300">
                          <span>{crop.app_display_name[0]}</span>
                        </div>} imageUrl={crop.image_url} className="w-10 h-10 rounded-full" />
                  </div>
                  <Paragraph className="ml-2" variant={VARIANT.BIG}>
                    {crop.app_display_name}
                  </Paragraph>
                </button>)}
            </section>}
        </div>

        {allCrops.map(cropsWithCategory => <CropCategoryAccordion key={cropsWithCategory.category} cropCategory={{
          ...cropsWithCategory,
          crops: [...cropsWithCategory.crops].filter(crop => crop.is_khetibook_enabled)
        }} crops={[NaN]} onClick={handleCropSelect} />)}

          <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="crops.tsx">
            <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="crops.tsx" />
          </ErrorModal>
        </section>
      </main>
    </>;
};
export const getServerSideProps: GetServerSideProps = async ({
  req: {
    cookies
  },
  locale
}) => {
  const accessToken = cookies[ACCESS_TOKEN_KEY];
  try {
    const allCrops = await fetchAllCropsV2(locale, FETCH_CROPS.ALL_CROPS, accessToken);
    return {
      props: {
        allCrops
      }
    };
  } catch {
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default AllCropsPage;